import React from 'react';
import { Link } from 'react-router-dom';
import { siteInfo, writings, movies, projects } from '../data/mock';
import { ArrowRight } from 'lucide-react';

// Social Icons as SVG for better control
const XIcon = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const LinkedInIcon = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current">
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
  </svg>
);

const InstagramIcon = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current">
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
  </svg>
);

const Home = () => {
  const recentWritings = writings.slice(0, 5);
  const recentMovies = movies.slice(0, 3);
  const featuredProjects = projects.slice(0, 2);

  return (
    <div>
      {/* Introduction */}
      <section className="mb-16">
        <h1 className="text-2xl font-serif mb-6">
          {siteInfo.intro.greeting} <span className="font-medium">{siteInfo.name.split(' ')[0]}.</span>
        </h1>
        <div className="text-[#a1a1aa] leading-relaxed space-y-4">
          <p>{siteInfo.intro.bio}</p>
          <p>
            {siteInfo.intro.previousWork}{' '}
            <a 
              href={siteInfo.intro.previousWorkLink.url} 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-white underline underline-offset-2 hover:text-[#c5c5d0] transition-colors"
            >
              {siteInfo.intro.previousWorkLink.text}
            </a>
            {siteInfo.intro.previousWorkDesc}
          </p>
          <p>
            {siteInfo.intro.hobbies}{' '}
            {siteInfo.intro.photosLink && (
              <Link
                to={siteInfo.intro.photosLink.url}
                className="text-white underline underline-offset-2 hover:text-[#c5c5d0] transition-colors"
              >
                {siteInfo.intro.photosLink.text}
              </Link>
            )}{' '}
            and{' '}
            <Link to={siteInfo.intro.travelLink.url} className="text-white underline underline-offset-2 hover:text-[#c5c5d0] transition-colors">
              {siteInfo.intro.travelLink.text}
            </Link>.
          </p>
        </div>

        {/* Social Links */}
        <div className="flex gap-4 mt-6">
          <a 
            href={siteInfo.social.twitter} 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-[#a1a1aa] hover:text-white transition-colors"
            aria-label="X (Twitter)"
          >
            <XIcon />
          </a>
          <a 
            href={siteInfo.social.linkedin} 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-[#a1a1aa] hover:text-white transition-colors"
            aria-label="LinkedIn"
          >
            <LinkedInIcon />
          </a>
          <a 
            href={siteInfo.social.instagram} 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-[#a1a1aa] hover:text-white transition-colors"
            aria-label="Instagram"
          >
            <InstagramIcon />
          </a>
        </div>
      </section>

      {/* Writing Section */}
      <section className="mb-16">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-serif">Writing</h2>
          <Link 
            to="/writing" 
            className="text-sm text-[#8f8fa3] hover:text-white transition-colors flex items-center gap-1"
          >
            See all <ArrowRight size={14} />
          </Link>
        </div>
        <div className="space-y-0">
          {recentWritings.map((article) => (
            <Link 
              key={article.id}
              to={`/writing/${article.slug}`}
              className="flex items-baseline py-3 border-b border-[#1f1f23] hover:bg-[#131316] -mx-2 px-2 transition-colors group"
            >
              <span className="text-sm text-[#8f8fa3] w-28 flex-shrink-0">
                <span className="hidden sm:inline">{article.displayDate}</span>
                <span className="sm:hidden">{article.shortDate}</span>
              </span>
              <span className="text-white group-hover:text-[#c5c5d0] transition-colors">
                {article.title}
              </span>
            </Link>
          ))}
        </div>
      </section>

      {/* About Section */}
      <section className="mb-16 space-y-4 text-[#c5c5d0] leading-relaxed">
        <h2 className="text-lg font-serif text-white">What I’m building</h2>
        <p>
          I split my time between Pleo’s product teams and a small studio called Useful Ventures. 
          My focus is on utilitarian software: collaboration tools, ambient AI, and finance workflows 
          that don’t require hand-holding. When I talk about{" "}
          <Link to="/projects" className="underline underline-offset-2 hover:text-white">
            projects
          </Link>
          , you’ll find case studies on CrewM8, a dating assistant, and a few workflow utilities.
        </p>
        <p>
          The{" "}
          <Link to="/movies" className="underline underline-offset-2 hover:text-white">
            movies log
          </Link>{" "}
          and{" "}
          <Link to="/travel" className="underline underline-offset-2 hover:text-white">
            travel journal
          </Link>{" "}
          exist because I like revisiting stories and hikes just as much as shipping features. They’re a 
          reminder that great product work borrows from cinema, literature, and long walks. If you’re curious 
          about the tools I rely on each week, the{" "}
          <Link to="/software" className="underline underline-offset-2 hover:text-white">
            software shelf
          </Link>{" "}
          lists my current stack.
        </p>
        <p>
          This site is intentionally text heavy. I want recruiters, collaborators, and curious friends to see 
          more than a hero image. Expect deep dives into how we run experiments at Pleo, what I’ve learned in the 
          Indian startup ecosystem, and notes on building with small, opinionated teams. If any of that resonates, 
          send a note—good conversations often turn into the next release.
        </p>
      </section>

      {/* Projects Section */}
      <section className="mb-16">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-serif">Projects</h2>
          <Link 
            to="/projects" 
            className="text-sm text-[#8f8fa3] hover:text-white transition-colors flex items-center gap-1"
          >
            See all <ArrowRight size={14} />
          </Link>
        </div>
        <div className="space-y-6">
          {featuredProjects.map((project) => (
            <div key={project.id} className="pb-4 border-b border-[#1f1f23]">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-medium text-white">
                    {project.url && project.url !== '#' ? (
                      <a
                        href={project.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hover:text-[#c5c5d0] transition-colors"
                      >
                        {project.name}
                      </a>
                    ) : (
                      project.name
                    )}
                  </h3>
                  <p className="text-sm text-[#8f8fa3] mt-1">{project.description}</p>
                  <div className="flex flex-wrap gap-2 mt-3">
                    {project.tech.map((tech) => (
                      <span
                        key={tech}
                        className="text-xs text-[#c5c5d0] bg-[#151518] px-2 py-0.5 rounded"
                      >
                        {tech}
                      </span>
                    ))}
                  </div>
                </div>
                <span className="text-sm text-[#7a7a86]">{project.year}</span>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Favorite Movies Section */}
      <section className="mb-8">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-serif">Most Liked Films</h2>
          <Link 
            to="/movies" 
            className="text-sm text-[#8f8fa3] hover:text-white transition-colors flex items-center gap-1"
          >
            See list <ArrowRight size={14} />
          </Link>
        </div>
        <div className="space-y-0">
          {recentMovies.map((movie) => (
            <div 
              key={movie.id}
              className="py-3 border-b border-[#1f1f23]"
            >
              <span className="font-medium text-white">{movie.title}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Contact */}
      <section className="mt-16 pt-8 border-t border-[#1f1f23]">
        <h2 className="text-lg font-serif mb-4">Say hello</h2>
        <p className="text-sm text-[#8f8fa3] mb-4">Have a project or idea? Drop me a note.</p>
        <a
          href="mailto:me@koras.world"
          className="inline-flex items-center gap-2 text-sm font-medium text-[#050505] bg-white px-4 py-2 rounded hover:bg-[#c5c5d0] transition-colors"
        >
          me@koras.world
        </a>
      </section>
    </div>
  );
};

export default Home;
